
import { useCallback } from 'react';
import { isSafari } from '@/utils/platformUtils';
import { useToast } from './use-toast';

export const useAudioPermissions = () => {
  const { toast } = useToast();

  const requestMicrophonePermission = useCallback(async () => {
    try {
      // First check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast({
          title: "Navegador no compatible",
          description: "Tu navegador no permite el acceso al micrófono. Intenta con Chrome o Safari.",
          variant: "destructive",
          duration: 6000
        });
        return null;
      }

      // Check if we're on HTTPS or localhost
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        toast({
          title: "Conexión no segura",
          description: "El micrófono solo funciona en conexiones HTTPS.",
          variant: "destructive",
          duration: 6000
        });
        return null;
      }

      console.log('Requesting microphone permission...');
      
      // Request permission with specific constraints for better mobile support
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });
      
      if (isSafari()) {
        console.log('Storing audio stream for Safari');
        window.streamReference = stream;
      }

      // Check if the stream is actually active
      const audioTracks = stream.getAudioTracks();
      if (!audioTracks || audioTracks.length === 0) {
        toast({
          title: "Error de micrófono",
          description: "No se detectó ningún micrófono activo.",
          variant: "destructive"
        });
        return null;
      }

      return stream;
    } catch (error: any) {
      console.error('Microphone permission error:', error);
      
      // Handle specific permission errors with detailed guidance
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast({
          title: "Permiso denegado",
          description: "Ve a Configuración → Safari/Chrome → Permisos → Micrófono y actívalo para este sitio.",
          variant: "destructive",
          duration: 8000
        });
      } else if (error.name === 'NotFoundError') {
        toast({
          title: "Micrófono no encontrado",
          description: "No se detectó ningún micrófono. Verifica que tu dispositivo tenga uno activo.",
          variant: "destructive",
          duration: 6000
        });
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast({
          title: "Micrófono en uso",
          description: "El micrófono está siendo usado por otra aplicación. Ciérrala e intenta de nuevo.",
          variant: "destructive",
          duration: 6000
        });
      } else {
        toast({
          title: "Error de micrófono",
          description: `${error.message || 'Error desconocido'}. Intenta recargar la página.`,
          variant: "destructive",
          duration: 6000
        });
      }
      return null;
    }
  }, [toast]);

  return { requestMicrophonePermission };
};
